// src/pages/ResultsPage.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { db } from '../firebase';
import { doc, onSnapshot } from "firebase/firestore";
import { FaMobileAlt, FaCube } from 'react-icons/fa';

/* ============================================================
   ANIMATED GRADIENT BACKGROUND
============================================================ */

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const GradientWrapper = styled.div`
  background: linear-gradient(135deg, #0d1020, #151a2d, #1d2340, #14182b);
  background-size: 300% 300%;
  animation: ${gradientAnimation} 12s ease-in-out infinite;
  min-height: 100vh;
  padding-top: 5rem;
  padding-bottom: 5rem;
`;

/* ============================================================
   PAGE STRUCTURE
============================================================ */

const PageContainer = styled.div`
  padding: 0 10%;
  @media (max-width: 768px) {
    padding: 0 5%;
  }
`;

const HeaderSection = styled.section`
  text-align: center;
  padding: 2rem 0 1rem 0;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  color: white;
  letter-spacing: -1px;
  text-shadow: 0px 0px 15px rgba(90,110,255,0.25);

  @media (max-width: 768px) {
    font-size: 2.7rem;
  }
`;

const IntroText = styled(motion.p)`
  font-size: 1.2rem;
  color: #cfd3e8;
  max-width: 700px;
  margin: 2rem auto 4rem auto;
  line-height: 1.7;
`;

/* ============================================================
   STATS CARD (GLASSMORPHISM)
============================================================ */

const StatsCard = styled(motion.div)`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 2.5rem;
  margin-bottom: 4rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const PortfolioGrowth = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2rem;
    text-align: center;
  }
`;

const GrowthPercentage = styled.div`
  h2 {
    font-size: 1.3rem;
    color: #cfd3e8;
    margin: 0 0 0.5rem 0;
  }
  p {
    font-size: 3rem;
    font-weight: 700;
    margin: 0;
    color: #4ade80;
  }
  small {
    font-size: 0.8rem;
    color: #9ca3af;
  }
`;

const TotalSignals = styled.div`
  text-align: right;
  p {
    font-size: 3rem;
    font-weight: 700;
    margin: 0;
  }
  small {
    font-size: 0.9rem;
    color: #cbd5e1;
  }

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatItem = styled.div`
  p {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
    color: ${props => props.color || 'white'};
  }
  small {
    font-size: 0.9rem;
    color: #a1a5b8;
  }
`;

/* ============================================================
   HOW IT WORKS SECTION
============================================================ */

const StepsSection = styled.section`
  padding: 4rem 0;
  text-align: center;
`;

const StepsTitle = styled.h2`
  color: white;
  font-size: 2.4rem;
  margin-bottom: 3rem;
`;

const StepsWrapper = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const StepCard = styled.div`
  background: rgba(255,255,255,0.05);
  padding: 2.5rem;
  border-radius: 20px;
  width: 100%;
  max-width: 320px;
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(12px);
  text-align: center;

  h3 {
    color: white;
    margin: 1rem 0;
  }

  p {
    color: #cfd3e8;
  }
`;

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(90deg,#576bff,#7385ff);
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.4rem;
  color: white;
  font-weight: 700;
`;

/* ============================================================
   WHO IS IT FOR SECTION
============================================================ */

const AudienceSection = styled.section`
  padding: 4rem 0 2rem 0;
  text-align: center;
`;

const AudienceTitle = styled.h2`
  color: white;
  font-size: 2.3rem;
  margin-bottom: 3rem;
`;

const AudienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AudienceCard = styled.div`
  background: rgba(255,255,255,0.05);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(12px);

  h3 {
    margin-bottom: 0.7rem;
    color: white;
  }

  p {
    color: #cfd3e8;
    line-height: 1.6;
  }
`;

/* ============================================================
   PERFORMANCE COMPARISON CHART
============================================================ */

const ComparisonContainer = styled.section`
  padding: 4rem 0;
  text-align: center;
`;

const ComparisonTitle = styled.h2`
  color: white;
  font-size: 2.4rem;
  margin-bottom: 3rem;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ComparisonItem = styled.div`
  backdrop-filter: blur(12px);
  padding: 2rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;

  h4 {
    color: white;
    margin-bottom: 1rem;
  }

  small {
    color: #cfd3e8;
  }
`;

const BarBase = styled.div`
  height: 12px;
  width: 100%;
  background: rgba(255,255,255,0.08);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 0.6rem;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${props => props.width};
  background: ${props => props.color};
  border-radius: 10px;
  transition: width 1s ease;
`;

/* ============================================================
   PHILOSOPHY SECTION (MARKETING FOCUSED)
============================================================ */

const PhilosophySectionContainer = styled.section`
  text-align: center;
  padding: 4rem 0;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.8rem;
  margin-bottom: 1.5rem;
  color: white;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  color: #cfd3e8;
  max-width: 700px;
  margin: 0 auto 4rem auto;
  font-size: 1.2rem;
  line-height: 1.7;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  text-align: left;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContentCard = styled(motion.div)`
  background: rgba(255,255,255,0.05);
  border-radius: 18px;
  padding: 2.5rem;
  border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(12px);
  transition: 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 45px rgba(90,110,255,0.3);
  }
`;

const IconWrapper = styled.div`
  font-size: 2.8rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
  filter: drop-shadow(0px 0px 8px rgba(90,110,255,0.4));
`;

const CardTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: white;
`;

const CardDescription = styled.p`
  color: #cfd2e6;
  line-height: 1.8;
`;

/* ============================================================
   CTA (HIGH-CONVERSION)
============================================================ */

const CtaSection = styled.section`
  text-align: center;
  padding: 5rem 10%;
  background: rgba(255,255,255,0.04);
  border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(14px);
  margin-top: 4rem;
  box-shadow: 0 20px 50px rgba(0,0,0,0.2);
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: white;
`;

const CtaSubtitle = styled.p`
  font-size: 1.2rem;
  color: #cfd3e8;
  max-width: 600px;
  margin: 0 auto 2.5rem auto;
`;

const CTAStatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 2.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const CTAStat = styled.div`
  text-align: center;

  h3 {
    font-size: 2rem;
    margin: 0;
    color: #7b8aff;
  }
  p {
    color: #cfd3e8;
    margin-top: 0.3rem;
    opacity: 0.9;
  }
`;

const CtaFooter = styled.p`
  margin-top: 1.5rem;
  color: #9ca3af;
  font-size: 0.9rem;
`;

const CtaButton = styled.a`
  display: inline-block;
  padding: 1.2rem 3.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.2rem;
  text-decoration: none;
  background: linear-gradient(90deg, #576bff, #7385ff);
  color: white;
  transition: 0.3s ease;
  box-shadow: 0 12px 32px rgba(90,110,255,0.35);

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 18px 45px rgba(90,110,255,0.55);
  }
`;

/* ============================================================
   ANIMATED NUMBER COMPONENTS (UNCHANGED)
============================================================ */

const AnimatedNumber = ({ value }) => {
  const ref = React.useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { damping: 100, stiffness: 100 });
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView) motionValue.set(value);
  }, [isInView, motionValue, value]);

  useEffect(() => springValue.on("change", (latest) => {
    if (ref.current) ref.current.textContent = `+${latest.toFixed(2)}%`;
  }), [springValue]);

  return <p ref={ref}>+0.00%</p>;
};

const AnimatedInteger = ({ value }) => {
  const ref = React.useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { damping: 50, stiffness: 100 });
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (isInView) motionValue.set(value);
  }, [isInView, motionValue, value]);

  useEffect(() => springValue.on("change", (latest) => {
    if (ref.current) ref.current.textContent = Math.round(latest);
  }), [springValue]);

  return <p ref={ref}>0</p>;
};

/* ============================================================
   MAIN COMPONENT
============================================================ */

const ResultsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const statsDocRef = doc(db, "statistics", "liveResults");
    const unsubscribe = onSnapshot(
      statsDocRef,
      (doc) => {
        if (doc.exists()) setStats(doc.data());
        else console.error("Stats document missing!");
        setLoading(false);
      },
      (error) => {
        console.error("Firebase Error:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <GradientWrapper>
        <PageContainer>
          <h2 style={{textAlign: 'center', paddingTop: '5rem', color: 'white'}}>
            Loading Results...
          </h2>
        </PageContainer>
      </GradientWrapper>
    );
  }

  if (!stats) {
    return (
      <GradientWrapper>
        <PageContainer>
          <h2 style={{textAlign: 'center', paddingTop: '5rem', color: 'white'}}>
            Could not load results. Please check back later.
          </h2>
        </PageContainer>
      </GradientWrapper>
    );
  }

  return (
    <GradientWrapper>
      <PageContainer>

        <Helmet>
  <title>Trend Genie AI — Live Trading Results, AI Signals & Real-Time Analysis</title>

  <meta name="description" content="Explore real-time performance of Trend Genie's AI trading signals. No backtests — live results, real signals, high-probability trades. Start your free trial today." />

  <meta name="keywords" content="AI trading app, crypto signals, stock trading signals, Trend Genie, automated trading alerts, forex signals, real time AI analysis" />

  <meta property="og:title" content="Trend Genie AI — Real-Time Trading Signals" />
  <meta property="og:description" content="Real-time AI signals with TP/SL, automated market scanning, and consistent performance. Join thousands trading smarter." />
  <meta property="og:image" content="https://your-domain.com/og-image.png" />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://trendgenie.app/results" />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Trend Genie AI — Real-Time Trading Signals" />
  <meta name="twitter:description" content="AI-powered crypto & stock signals updated in real time." />
  <meta name="twitter:image" content="https://your-domain.com/og-image.png" />

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      "name": "Trend Genie",
      "operatingSystem": "Android",
      "applicationCategory": "FinanceApplication",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1500"
      },
      "url": "https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie",
      "description": "AI-powered trading signals for crypto and stocks. Real-time alerts with precise entry, TP, and SL levels."
    })}
  </script>
</Helmet>


        {/* ---------------- HEADER ---------------- */}
        <HeaderSection>
          <PageTitle initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            Our Proven Track Record
          </PageTitle>

          <IntroText initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            These results are 100% real and updated continuously through our automated systems.
            No backtests. No cherry-picked data. Just pure live performance.
          </IntroText>
        </HeaderSection>

        {/* ---------------- STATS CARD ---------------- */}
        <StatsCard initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
          <PortfolioGrowth>
            <GrowthPercentage>
              <h2>Portfolio Growth</h2>
              <AnimatedNumber value={stats.growth} />
              <small>Assuming $100 per trade & 10x leverage</small>
            </GrowthPercentage>

            <TotalSignals>
              <AnimatedInteger value={stats.totalSignals} />
              <small>Total Signals</small>
            </TotalSignals>
          </PortfolioGrowth>

          <StatsGrid>
            <StatItem color="#facc15">
              <AnimatedInteger value={stats.pending} />
              <small>Pending</small>
            </StatItem>

            <StatItem color="#60a5fa">
              <AnimatedInteger value={stats.active} />
              <small>Active</small>
            </StatItem>

            <StatItem>
              <AnimatedInteger value={stats.invalidated} />
              <small>Invalidated</small>
            </StatItem>

            <StatItem color="#34d399">
              <AnimatedInteger value={stats.tp1Hits} />
              <small>TP1 Hits</small>
            </StatItem>

            <StatItem color="#34d399">
              <AnimatedInteger value={stats.tp2Hits} />
              <small>TP2 Hits</small>
            </StatItem>

            <StatItem color="#f87171">
              <AnimatedInteger value={stats.slHits} />
              <small>SL Hits</small>
            </StatItem>
          </StatsGrid>
        </StatsCard>

        {/* ---------------- HOW IT WORKS ---------------- */}
        <StepsSection>
          <StepsTitle>How Trend Genie Works</StepsTitle>

          <StepsWrapper>

            <StepCard>
              <StepNumber>1</StepNumber>
              <h3>AI Scans the Market</h3>
              <p>Millions of data points analyzed 24/7 — faster than any human.</p>
            </StepCard>

            <StepCard>
              <StepNumber>2</StepNumber>
              <h3>Signal Is Generated</h3>
              <p>Get clear entries, TP1, TP2, and SL instantly inside the app.</p>
            </StepCard>

            <StepCard>
              <StepNumber>3</StepNumber>
              <h3>You Execute the Trade</h3>
              <p>Just follow the signal — no stress, no confusion, no emotions.</p>
            </StepCard>

          </StepsWrapper>
        </StepsSection>

        {/* ---------------- WHO IS IT FOR ---------------- */}
        <AudienceSection>
          <AudienceTitle>Who Is Trend Genie Designed For?</AudienceTitle>

          <AudienceGrid>
            <AudienceCard>
              <h3>📱 Busy Traders</h3>
              <p>No time to watch charts all day? We scan the market for you.</p>
            </AudienceCard>

            <AudienceCard>
              <h3>📈 Beginners</h3>
              <p>Get easy-to-follow entries with clear targets and stop-loss levels.</p>
            </AudienceCard>

            <AudienceCard>
              <h3>💼 Part-Time Traders</h3>
              <p>Trade confidently—even with limited time and market knowledge.</p>
            </AudienceCard>

            <AudienceCard>
              <h3>🤖 Data-Driven Traders</h3>
              <p>If you prefer facts over feelings, Trend Genie is made for you.</p>
            </AudienceCard>
          </AudienceGrid>
        </AudienceSection>

        {/* ---------------- PERFORMANCE COMPARISON ---------------- */}
        <ComparisonContainer>
          <ComparisonTitle>Trend Genie vs Traditional Trading</ComparisonTitle>

          <ComparisonGrid>

            <ComparisonItem>
              <h4>Speed</h4>
              <BarBase>
                <BarFill width="95%" color="#34d399" />
              </BarBase>
              <small>AI reacts instantly</small>
            </ComparisonItem>

            <ComparisonItem>
              <h4>Accuracy Consistency</h4>
              <BarBase>
                <BarFill width="88%" color="#60a5fa" />
              </BarBase>
              <small>Eliminates emotional errors</small>
            </ComparisonItem>

            <ComparisonItem>
              <h4>Stress Level</h4>
              <BarBase>
                <BarFill width="20%" color="#f87171" />
              </BarBase>
              <small>Less fear, less guessing</small>
            </ComparisonItem>

          </ComparisonGrid>
        </ComparisonContainer>

        {/* ---------------- PHILOSOPHY SECTION ---------------- */}
        <PhilosophySectionContainer>
          <SectionTitle initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
            Why Traders Are Switching to Trend Genie
          </SectionTitle>

          <SectionSubtitle initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
            Trend Genie is built to remove stress, emotion, and guesswork from trading — giving you
            simple, powerful, real-time AI signals that anyone can follow.
          </SectionSubtitle>

          <Grid>
            <ContentCard initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }}>
              <IconWrapper><FaMobileAlt /></IconWrapper>
              <CardTitle>Your Edge in the Market — Delivered Fast</CardTitle>
              <CardDescription>
                Trend Genie scans markets 24/7 and instantly detects profitable setups
                long before manual traders notice them. Clear entries, targets, and stop-loss
                help you trade confidently — even if you're a beginner.
                <br /><br />
                ✔ Real-time AI signals <br />
                ✔ Automatic TP & SL <br />
                ✔ Beginner-friendly <br />
                ✔ 10,000+ data checks per day
              </CardDescription>
            </ContentCard>

            <ContentCard initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }}>
              <IconWrapper><FaCube /></IconWrapper>
              <CardTitle>Emotionless Trading Wins More</CardTitle>
              <CardDescription>
                Most traders lose because of emotional decisions like fear and greed.
                Trend Genie removes emotion completely and reacts instantly based on pure data.
                <br /><br />
                ✔ Zero emotion <br />
                ✔ Faster responses <br />
                ✔ Data-backed decisions <br />
                ✔ Designed for busy traders
              </CardDescription>
            </ContentCard>
          </Grid>
        </PhilosophySectionContainer>

        {/* ---------------- FINAL CTA ---------------- */}
        <CtaSection>
          <CtaTitle>Start Trading Smarter — Today</CtaTitle>

          <CtaSubtitle>
            Trend Genie gives you real-time AI signals, automated TP/SL levels,
            and emotion-free trading. Join thousands of traders upgrading their results.
          </CtaSubtitle>

          <CTAStatsRow>
            <CTAStat>
              <h3>10k+</h3>
              <p>Signals Analyzed Daily</p>
            </CTAStat>
            <CTAStat>
              <h3>24/7</h3>
              <p>AI Market Scanning</p>
            </CTAStat>
            <CTAStat>
              <h3>92%</h3>
              <p>User Satisfaction</p>
            </CTAStat>
          </CTAStatsRow>

          <CtaButton
            href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
            target="_blank"
          >
            Download Trend Genie
          </CtaButton>

          <CtaFooter>
            7-Day Free Trial · Cancel Anytime · Instant Activation
          </CtaFooter>
        </CtaSection>

      </PageContainer>
    </GradientWrapper>
  );
};

export default ResultsPage;
